import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useFetchData from '../../hooks/useFetchData';
import { useLoading } from '../../context/LoadingContext';
import useFullScreen from '../../hooks/useFullscreen';
import styles from './capsule.module.scss';
import Toolbar from '../Toolbar/Toolbar';
import { ContentLayoutProvider, useContentLayout } from '../../context/ContentLayout';
import { Helmet } from 'react-helmet-async';

const dilutionsData = [
    {
        letter: "A",
        encoding: "! Atténuer, Contraire légitime, Efficace",
        sort: 1
    },
    {
        letter: "R",
        encoding: "! Renforcer, normal, Efficace",
        sort: 2
    },
    {
        letter: "S",
        encoding: "! Syntoniser, Dans son assertion allégorique, Efficace",
        sort: 3
    },
    {
        letter: "E",
        encoding: "! Effluence, Normal, Répétition",
        sort: 4
    },
    {
        letter: "T-08 E",
        encoding: "! 40/3.2, Profond, Interaction",
        sort: 5
    },
    {
        letter: "T-10 E",
        encoding: "! 40/4, Transmutation , réactiver",
        sort: 6
    },
    {
        letter: "T-12 E",
        encoding: "! 40/4.8, Interaction, réactiver",
        sort: 7
    },
    {
        letter: "Sans",
        encoding: "! 1:1, Dans son assertion allégorique, Efficace  ",
        sort: 8
    },
    // Ajoutez d'autres dilutions au besoin
];

const dilutions = {};
dilutionsData.forEach((dilution) => {
    dilutions[dilution.letter] = dilution;
});

const CapsuleDisplayed = () => {
    const { id } = useParams();
    const { data, error, isLoading } = useFetchData(`/capsules/${id}`);
    const { layoutConfig, checkColumns, resetFontsize, resetColumn, checkFontsize } = useContentLayout();
    const [currentDilution, setCurrentDilution] = useState(null);
    const [isDisplay, setIsDisplay] = useState(true);


    const containerRef = useRef(null);



    const content = useMemo(() => {
        return data?.configurations?.[0]?.configuration?.content;
    }, [data, currentDilution]);

    // Fonction utilitaire pour obtenir la première dilution disponible selon l'ordre défini
    const getFirstAvailableDilution = useCallback((contentData) => {
        if (!contentData || contentData.length === 0) return null;

        // Récupérer toutes les dilutions uniques disponibles dans le contenu
        const availableDilutions = new Set();
        contentData.forEach(item => {
            const dilutionLabel = item?.dilution?.label || item?.mixture?.defaultDilution?.label;
            if (dilutionLabel) {
                availableDilutions.add(dilutionLabel);
            }
        });

        // Trier les dilutions disponibles selon l'ordre défini dans dilutionsData
        const sortedAvailableDilutions = dilutionsData
            .filter(dilution => availableDilutions.has(dilution.letter))
            .sort((a, b) => a.sort - b.sort);

        // Retourner la première dilution selon l'ordre de priorité
        return sortedAvailableDilutions.length > 0 ? dilutions[sortedAvailableDilutions[0].letter] : null;
    }, []);

    const sortedData = useMemo(() => {
        if (!isLoading && content) {
            if (!currentDilution) {
                const firstAvailableDilution = getFirstAvailableDilution(content);
                if (firstAvailableDilution) {
                    setCurrentDilution(firstAvailableDilution);
                }
            }
            return currentDilution
                ? content.filter(item => (item?.dilution?.label || item?.mixture?.defaultDilution?.label) === currentDilution.letter)
                : content;
        }
        return [];
    }, [content, currentDilution, isLoading, getFirstAvailableDilution]);

    const getFirstDilution = () => {
        if (!data?.configurations?.[0]?.configuration?.content) return;

        const versionContent = data?.configurations?.[0]?.configuration?.content;
        const firstAvailableDilution = getFirstAvailableDilution(versionContent);
        if (firstAvailableDilution) {
            setCurrentDilution(firstAvailableDilution);
        }
    }



    const toggleToolbar = (event) => {
        if (event.ctrlKey && event.code === 'KeyA') {
            setIsDisplay(!isDisplay);
        }
    };

    const resetContentStyles = () => {
        const container = containerRef.current;
        if (container) {
            container.style.columnCount = 1;
        }
    };

    const checkWindowSize = () => {
        resetContentStyles();
    };

    useEffect(() => {
        document.addEventListener('keydown', toggleToolbar);
        return () => {
            document.removeEventListener('keydown', toggleToolbar);
        };
    }, [isDisplay]);



    useEffect(() => {
        checkColumns();
        checkFontsize();
    }, [sortedData, currentDilution]);




    if (isLoading) {
        return <p>Chargement...</p>;
    }
    return (
        <>
            <Helmet>
                <title>{data?.customer?.firstname} {data?.customer?.lastname} - {data?.name}</title>
            </Helmet>

            {error && <p>{error.request.status === 403 ? 'Unauthorized' : error.message}</p>}
            {isDisplay && (

                <Toolbar
                    checkWindowSize={checkWindowSize}
                    fontSize={layoutConfig.fontSize}
                    columnCount={layoutConfig.columns}
                    content={content}
                    dilutionsData={dilutionsData}
                    currentDilution={currentDilution}
                    setCurrentDilution={setCurrentDilution}
                    resetColumn={resetColumn}
                    resetFontsize={resetFontsize}
                    capsuleName={data?.name}
                    capsuleId={data?.id}
                    customerName={`${data?.customer?.firstname || ''} ${data?.customer?.lastname || ''}`.trim()}
                />

            )}

            <div style={{ padding: '20px' }}>
                <div ref={containerRef} className={styles.container} style={{ fontSize: `${layoutConfig.fontSize}px`, columnCount: layoutConfig.columns }}>
                    {sortedData && sortedData.map((item, index) => {
                        const encodage = item.dilution ? item.dilution.encodage : item.mixture.defaultDilution.encodage
                        return (
                            <div key={index}>
                                <p>{item.mixture?.subCategory?.toUpperCase()}</p>
                                <p>{item.mixture?.name} {encodage} {"{"}</p>
                                {item.mixture?.reagents?.map((reagent, reagentIndex) => (
                                    <p key={`reagent_${reagentIndex}`}>{reagent}</p>
                                ))}
                                <p>{"}"}</p>
                                <br />
                                <br />
                            </div>
                        )
                    })}
                </div>
            </div>
        </>
    );
};

const CapsuleEncoder = () => {
    return (
        <ContentLayoutProvider>
            <CapsuleDisplayed />
        </ContentLayoutProvider>
    );
};

export default CapsuleEncoder;
